import React, { Component } from 'react';
import Style from './index.less';
import classNames from 'classnames';
import i18n from '@i18next/i18n';
import Tooltip from '@tencent/dui/lib/components/Tooltip';
import { getGradFillConfigs, getShadeGradFillConfigs, getTintGradFillConfigs, IGradFillConfig } from './style-config';
import { GradientType, IBackgroundFillInfo, IGradFill } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { ColorConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/color/color-converter';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { ISlideDataModel, STSchemeColorVal } from '@tencent/slide-sdk-model';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import isEqual from 'lodash/isEqual';
import { GradientPresetItem, GradualList } from '@src/framework/workbench/drawer/components/gradual-preset';

interface GradFillPresetMenuProps {
    fillStyle: IBackgroundFillInfo;
    onGradFillPresetStyleClick: (value: IBackgroundFillInfo) => void;
    slideModel: ISlideModel;
}

export default class GradFillPresetMenu extends Component<GradFillPresetMenuProps> {
    public render() {
        const { solidFill, gradFill } = this.props.fillStyle || {};
        const solidFillColor = solidFill?.color;
        let noGradFill;
        if (solidFill) {
            noGradFill = solidFillColor;
        } else if (gradFill) {
            noGradFill = gradFill.gsList[0].color;
        } else {
            noGradFill = new TDColor({ themeKey: STSchemeColorVal.accent1 });
        }
        return (
            <div className={classNames(Style.container, 'grad-fill-preset')}>
                <div className={Style['grad-fill-type-title']}>{i18n.t('无渐变')}</div>
                <div className={Style['grad-fill-collection']}>
                    <div className={classNames({
                        [Style['grad-fill-collection-item']]: true,
                        [Style['grad-fill-item-selected']]: !gradFill,
                    })}
                    onClick={() => this.handleGradFillPresetIconClick({ solidFill: { color: noGradFill } })}
                    >
                        <Tooltip placement={['top', 'right']} title={i18n.t('无渐变')}>
                            <div className={Style['preset-icon']}>
                                <div style={{ background: this.getColor(noGradFill) }}></div>
                            </div>
                        </Tooltip>
                    </div>
                </div>
                {
                    gradFill ? <>
                        <div className={Style['grad-fill-type-title']}>{i18n.t('变体')}</div>
                        <div className={Style['grad-fill-collection']}>
                            {
                                getGradFillConfigs(gradFill.gsList).map(item => this.renderGradFillPreset(item))
                            }
                        </div>
                    </> : <>
                        <div className={Style['grad-fill-type-title']}>{i18n.t('浅色变体')}</div>
                        <div className={Style['grad-fill-collection']}>
                            {
                                getTintGradFillConfigs(noGradFill).map(item => this.renderGradFillPreset(item))
                            }
                        </div>
                        <div className={Style['grad-fill-type-title']}>{i18n.t('深色变体')}</div>
                        <div className={Style['grad-fill-collection']}>
                            {
                                getShadeGradFillConfigs(noGradFill).map(item => this.renderGradFillPreset(item))
                            }
                        </div>
                    </>
                }
                <div className={Style['grad-fill-type-title']}>{i18n.t('推荐渐变')}</div>
                { this.renderColorPicker()}
            </div>
        );
    }

    private renderColorPicker = () => {
        const colors = this.getPresetItemList();
        return <div className={Style['grad-fill-color-picker-container']}>
            {
                colors.map(colorItem => <div
                    className={Style['grad-fill-color-picker-item']}
                    key={colorItem.item.style}
                    style={{ background: colorItem.color }}
                    onClick={() => {
                        this.handleGradFillPresetIconClick({
                            gradFill: this.getGradFillFromItem(colorItem.item),
                        });
                    }}
                />)
            }
        </div>;
    };

    private getPresetItemList = () => GradualList.map((presetItem) => {
        let gradientBg = `linear-gradient(${presetItem.angle}deg,`;
        for (let i = 0; i < presetItem.list.length; i++) {
            const posItem = presetItem.list[i];
            gradientBg += `${posItem.color} ${posItem.pos}%`;
            if (i < presetItem.list.length - 1) {
                gradientBg += ',';
            }
        }
        gradientBg += ')';
        return {
            color: gradientBg,
            item: presetItem,
        };
    });

    private getGradFillFromItem = (item: GradientPresetItem): IGradFill => {
        const gradFill = {
            gsList: item.list.map(posItem => ({
                color: new TDColor({ rgbaStr: posItem.color }),
                pos: posItem.pos,
            })),
            lin: {
                ang: item.angle,
            },
            pathGradient: {
                path: GradientType.Linear,
            },
        };
        return gradFill as IGradFill;
    };

    private renderGradFillPreset(item: IGradFillConfig) {
        const { gradFill } = this.props.fillStyle || {};
        const isSelected = isEqual(item.gradFill.pathGradient, gradFill?.pathGradient)
            && isEqual(item.gradFill.lin, gradFill?.lin);
        const softEdgeItemClass = classNames({
            [Style['grad-fill-collection-item']]: true,
            [Style['grad-fill-item-selected']]: isSelected,
        });
        let background;
        const { gsList, lin, pathGradient } = item.gradFill;
        const gsColorStr = gsList?.map(gs => `${this.getColor(gs.color as ITDColor)} ${gs.pos}%`).join(', ');
        if (pathGradient?.path === GradientType.Linear) {
            background = `linear-gradient(${(lin?.ang || 0) - 270}deg, ${gsColorStr})`;
        } else if (pathGradient?.path === GradientType.Radial) {
            background = `radial-gradient(circle at ${pathGradient.fillToRect?.l || 0}%  ${pathGradient.fillToRect?.t || 0}%, ${gsColorStr})`;
        }
        return (
            <div
                key={item.title}
                className={softEdgeItemClass}
                onClick={() => this.handleGradFillPresetIconClick({ gradFill: item.gradFill as IGradFill })}
            >
                <Tooltip placement={['top', 'right']} title={i18n.t(item.title)}>
                    <div className={Style['preset-icon']}>
                        <div style={{ background }}></div>
                    </div>
                </Tooltip>
            </div>
        );
    }

    private getColor = (color: ITDColor): string => {
        const colorSdk = {};
        ColorConverter.convertPartialDataFromUiToSdk(
            color,
            colorSdk,
        );
        const { slideModel } = this.props;
        const colorStr =  SlideDataServices.SDKDataServices.ColorManager()?.resolveColor(
            colorSdk,
            slideModel?.sdkData as ISlideDataModel,
        )?.srgbClr?.val;
        return colorStr ? `#${colorStr}` : '';
    };

    private handleGradFillPresetIconClick = (style: IBackgroundFillInfo) => {
        this.props.onGradFillPresetStyleClick(style);
    };
}
